import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { db } from '../firebase/config';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import './Header.css';

function Header() {
  const { getCartCount } = useCart();
  const cartCount = getCartCount();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isShopExpanded, setIsShopExpanded] = useState(false);
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchMenuData = async () => {
      try {
        const categoriesRef = collection(db, 'categories');
        const categoriesSnap = await getDocs(query(categoriesRef, orderBy('order', 'asc')));
        const categoriesData = categoriesSnap.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategories([]);
      }

      try {
        const subcategoriesRef = collection(db, 'subcategories');
        const subcategoriesSnap = await getDocs(query(subcategoriesRef, orderBy('order', 'asc')));
        const subcategoriesData = subcategoriesSnap.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setSubcategories(subcategoriesData);
      } catch (error) {
        console.error('Error loading subcategories:', error);
        setSubcategories([]);
      }
    };

    fetchMenuData();
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isMenuOpen]);

  const subcategoriesByCategory = useMemo(() => {
    return subcategories.reduce((acc, subcategory) => {
      const categoryId = subcategory.categoryId || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = [];
      }
      acc[categoryId].push(subcategory);
      return acc;
    }, {});
  }, [subcategories]);

  const buildShopLink = (categoryId, subcategoryId) => {
    const params = new URLSearchParams();
    if (categoryId) params.set('category', categoryId);
    if (subcategoryId) params.set('subcategory', subcategoryId);
    const queryString = params.toString();
    return `/shop${queryString ? `?${queryString}` : ''}`;
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsShopExpanded(false);
    setExpandedCategoryId(null);
  };

  return (
    <header className={`site-header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="site-header__container">
        <div className="site-header__left">
          <Link to="/" className="site-header__brand">THE TAG</Link>
        </div>
        <nav className="site-header__nav">
          <Link to="/cart" className="cart-link">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </Link>
          <button
            className="site-menu-toggle"
            onClick={() => setIsMenuOpen(prev => !prev)}
            aria-label="Open menu"
            aria-expanded={isMenuOpen}
            type="button"
          >
            <span className="site-menu-toggle__icon"></span>
            <span className="site-menu-toggle__icon"></span>
            <span className="site-menu-toggle__icon"></span>
          </button>
        </nav>
      </div>

      <div className={`site-menu ${isMenuOpen ? 'is-open' : ''}`}>
        <button
          className="site-menu__backdrop"
          onClick={closeMenu}
          aria-label="Close menu"
          type="button"
        ></button>
        <div className="site-menu__panel">
          <div className="site-menu__header">
            <div className="site-menu__header-left">
              <Link to="/" className="site-menu__logo" onClick={closeMenu}>
                THE TAG
              </Link>
            </div>
            <button
              className="site-menu__close"
              onClick={closeMenu}
              aria-label="Close menu"
              type="button"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div className="site-menu__content">
            <Link to="/" className="site-menu__link" onClick={closeMenu}>
              Home
            </Link>
            <div className="site-menu__category-group">
              <div className="site-menu__shop-header">
                <button
                  className="site-menu__shop-toggle"
                  onClick={() => setIsShopExpanded(prev => !prev)}
                  aria-label="Toggle shop categories"
                  type="button"
                >
                  <span className="site-menu__shop-text">Shop</span>
                  {categories.length > 0 && (
                    <svg 
                      className={`site-menu__chevron ${isShopExpanded ? 'is-open' : ''}`} 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  )}
                </button>
              </div>
              <div className={`site-menu__categories-list ${isShopExpanded ? 'is-open' : ''}`}>
                  {categories.map(category => {
                    const categorySubcategories = subcategoriesByCategory[category.id] || [];
                    const isExpanded = expandedCategoryId === category.id;
                    return (
                      <div key={category.id} className="site-menu__category">
                        <div className="site-menu__category-row">
                          <Link
                            to={buildShopLink(category.id)}
                            className="site-menu__category-link"
                            onClick={closeMenu}
                          >
                            {category.label || category.id}
                          </Link>
                          {categorySubcategories.length > 0 && (
                            <button
                              className={`site-menu__category-toggle ${isExpanded ? 'is-open' : ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                setExpandedCategoryId(isExpanded ? null : category.id);
                              }}
                              aria-label={`Toggle ${category.label || 'category'} subcategories`}
                              type="button"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </button>
                          )}
                        </div>
                        {categorySubcategories.length > 0 && (
                          <div className={`site-menu__subcategory-list ${isExpanded ? 'is-open' : ''}`}>
                            {categorySubcategories.map(subcategory => (
                              <Link
                                key={subcategory.id}
                                to={buildShopLink(category.id, subcategory.id)}
                                className="site-menu__subcategory-link"
                                onClick={closeMenu}
                              >
                                {subcategory.label || subcategory.id}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
